/*
 * story-player.js — turns a [data-story] block into an Instagram-story style
 * swipeable player on mobile (<=749px). Each [data-story-slide] inside
 * [data-story-track] is a full-width slide. Adds progress bars, left/right tap
 * zones, optional auto-advance (data-story-autoplay="ms"), pause-on-hold, and
 * keeps the progress bars in sync when the user swipes. On desktop it does
 * nothing (the block renders in its normal layout).
 */
(function () {
  var mq = window.matchMedia('(max-width: 749px)');

  function Player(root) {
    this.root = root;
    this.track = root.querySelector('[data-story-track]') || root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-story-slide]'));
    this.interval = parseInt(root.getAttribute('data-story-autoplay'), 10) || 0;
    this.idx = 0;
    this.timer = null;
    this.paused = false;
    this.active = false;
    if (this.slides.length < 2) return;
    this.build();
    this.bind();
    this.activate();
  }

  Player.prototype.build = function () {
    var bars = document.createElement('div');
    bars.className = 'story-bars';
    this.bars = this.slides.map(function () {
      var b = document.createElement('div');
      b.className = 'story-bar';
      var i = document.createElement('i');
      b.appendChild(i);
      bars.appendChild(b);
      return i;
    });
    this.root.appendChild(bars);

    var mk = function (cls, label) {
      var z = document.createElement('button');
      z.type = 'button';
      z.className = 'story-tap ' + cls;
      z.setAttribute('aria-label', label);
      return z;
    };
    this.prevZone = mk('story-tap--prev', 'Previous');
    this.nextZone = mk('story-tap--next', 'Next');
    this.root.appendChild(this.prevZone);
    this.root.appendChild(this.nextZone);

    // animated "swipe" hint overlay to make the gesture obvious; dismissed on first move.
    this.hint = document.createElement('div');
    this.hint.className = 'story-hint';
    this.hint.innerHTML = '<span class="story-hint__hand" aria-hidden="true">👉</span><span class="story-hint__tx">Swipe to explore</span>';
    this.root.appendChild(this.hint);
  };

  Player.prototype.updateHint = function () {
    if (!this.hint) { return; }
    // keep the swipe hint on every slide; hide it only once the last slide is reached
    var onLast = this.idx >= this.slides.length - 1;
    this.hint.classList.toggle('story-hint--gone', onLast);
  };

  Player.prototype.bind = function () {
    var self = this;
    this.prevZone.addEventListener('click', function () { self.go(self.idx - 1); });
    this.nextZone.addEventListener('click', function () { self.go(self.idx + 1); });

    // keep idx in sync when the user swipes the native scroll-snap track
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            var i = self.slides.indexOf(e.target);
            if (i > -1 && i !== self.idx) { self.idx = i; self.paint(); self.restart(); }
          }
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach(function (s) { self.io.observe(s); });
    }

    ['touchstart', 'pointerdown'].forEach(function (ev) {
      self.track.addEventListener(ev, function () { self.pause(); }, { passive: true });
    });
    ['touchend', 'pointerup', 'touchcancel'].forEach(function (ev) {
      self.track.addEventListener(ev, function () { self.resume(); }, { passive: true });
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { self.pause(); } else { self.resume(); }
    });
  };

  Player.prototype.go = function (i) {
    if (i < 0) { i = 0; }
    if (i > this.slides.length - 1) { i = 0; } // loop back to the first slide
    this.idx = i;
    this.slides[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    this.paint();
    this.restart();
  };

  Player.prototype.paint = function () {
    for (var i = 0; i < this.bars.length; i++) {
      var el = this.bars[i];
      el.style.transition = 'none';
      el.style.width = i < this.idx ? '100%' : '0%';
    }
    var cur = this.bars[this.idx];
    if (!cur) { return; }
    if (this.interval && this.active && !this.paused) {
      void cur.offsetWidth; // reflow so the transition runs
      cur.style.transition = 'width ' + this.interval + 'ms linear';
      cur.style.width = '100%';
    } else if (!this.interval) {
      cur.style.width = '100%'; // manual mode: current bar shown full
    }
    this.updateHint();
  };

  Player.prototype.restart = function () {
    this.stop();
    if (!this.interval || this.paused || !this.active) { return; }
    var self = this;
    this.timer = setTimeout(function () { self.go(self.idx + 1); }, this.interval);
  };

  Player.prototype.stop = function () {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  };

  Player.prototype.pause = function () {
    if (this.paused || !this.active) { return; }
    this.paused = true;
    this.stop();
    var cur = this.bars[this.idx];
    if (cur) { var w = getComputedStyle(cur).width; cur.style.transition = 'none'; cur.style.width = w; }
  };

  Player.prototype.resume = function () {
    if (!this.paused) { return; }
    this.paused = false;
    this.paint();
    this.restart();
  };

  Player.prototype.activate = function () {
    var on = mq.matches;
    if (on === this.active) { return; }
    this.active = on;
    this.root.classList.toggle('story-on', on);
    if (on) { this.idx = 0; this.paint(); this.restart(); }
    else { this.stop(); }
  };

  function initAll() {
    var roots = document.querySelectorAll('[data-story]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.__storyPlayer) { root.__storyPlayer.activate(); }
      else { root.__storyPlayer = new Player(root); }
    }
  }

  if (document.readyState !== 'loading') { initAll(); }
  else { document.addEventListener('DOMContentLoaded', initAll); }
  if (mq.addEventListener) { mq.addEventListener('change', initAll); }
  else if (mq.addListener) { mq.addListener(initAll); }
})();
